import { useCallback, useMemo } from "react";

import { useQuery } from "@apollo/client";
import { Box, Flex, Stack } from "@mantine/core";
import { Button, Colors, Section, Switch, Tooltip } from "@snapchat/snap-design-system";
import { AddIcon, InformationIcon, Sizes, TrashIcon } from "@snapchat/snap-design-system-icons";
import { type GraphQLError } from "graphql";

import { CopyToClipboardButton, SkeletonContainer, buttonOffset } from "../../camkit/common/components";
import { useModal } from "../../camkit/core/hooks";
import { HostedLensStatus } from "../../gql-generated/types";
import { LensStatusIndicator } from "../../lens/components/shared";
import { ButtonDisabledTooltip } from "../../shared/components/ButtonDisabledTooltip";
import { getHostedLens } from "../api/query/getHostedLens";
import { type GetHostedLensQuery, type GetHostedLensQueryVariables } from "../api/query/getHostedLens.gql-generated";
import { useDisclaimerAndAcceptance } from "../hooks/useDisclaimerAndAcceptance";
import { useHostedLens } from "../hooks/useHostedLens";

import { HostedLensDisclaimerModal } from "./HostedLensDisclaimerModal";
import {
    Anchor,
    GrayLabel,
    bodyContainerCss,
    hostedLensHeaderCss,
    indicatorCSS,
    tooltipStatusWrapper,
} from "./HostedLensSection.styles";
import { HostedLensStatusCell } from "./HostedLensStatusCell";
import { HostedLensDisclaimerEnum, type HostedLensDisclaimerOptions } from "./disclaimerOptions";
import {
    DeleteHeader,
    GenerateHeader,
    HostedLensBody,
    HostedLensDetailsHeader,
    HostedLensDisabledTooltipText,
    SectionLensEnableLabel,
    SectionLensEnableTooltipContent,
    SectionLensStatusLabel,
    SectionLensURLLabel,
    SectionLensURLTooltipContent,
    SectionStatusTooltipContent,
    SectionStatusTooltipItemOne,
    SectionStatusTooltipItemTwo,
} from "./messages.generated";

interface HostedLensSectionProps {
    organizationId: string;
    lensID: string;
    unlockableId?: number;
    disableDelete?: boolean;
    disableGenerate?: boolean;
}

const BREAKPOINT = "md";

export const HostedLensSection: React.FC<HostedLensSectionProps> = ({
    organizationId,
    lensID,
    unlockableId,
    disableDelete,
    disableGenerate,
}) => {
    const [showDisclaimerProps, showDisclaimerModal, option] = useModal<HostedLensDisclaimerOptions>();
    const { handleToggle, handleErrors, isEnabled } = useDisclaimerAndAcceptance({
        option,
        showDisclaimerModal,
    });

    const { updateHostedLensStatus, createHostedLens, deleteHostedLensLink, isUpdating } = useHostedLens();

    const { data, loading } = useQuery<GetHostedLensQuery, GetHostedLensQueryVariables>(getHostedLens, {
        variables: { lensID, organizationId },
        errorPolicy: "all",
        fetchPolicy: "network-only",
    });

    const hostedLens = useMemo(() => data?.getHostedLens.lens, [data]);

    const updateOrGenerateHostedLens = useCallback(async () => {
        let errors: readonly GraphQLError[] | undefined = [];
        try {
            const variables = { lensID: hostedLens ? hostedLens.id : lensID };
            const status = isEnabled
                ? HostedLensStatus.HostedLensStatusPublished
                : HostedLensStatus.HostedLensStatusDraft;
            const res = hostedLens
                ? await updateHostedLensStatus({ variables: { ...variables, status } })
                : await createHostedLens({ variables, refetchQueries: [getHostedLens] });
            errors = res.errors;
        } catch (error: unknown) {
            errors = [error as GraphQLError];
        }
        handleErrors(errors);
    }, [createHostedLens, handleErrors, hostedLens, isEnabled, lensID, updateHostedLensStatus]);

    const onModalAcceptance = useCallback(
        async (isAccepted: boolean) => {
            if (isAccepted) {
                if (option === HostedLensDisclaimerEnum.DELETE && hostedLens) {
                    try {
                        const promise = await deleteHostedLensLink({
                            variables: { lensID: hostedLens.id, organizationId },
                            refetchQueries: [getHostedLens],
                        });
                        if (promise.errors) {
                            handleErrors(promise.errors);
                        }
                    } catch (error) {
                        handleErrors([error as GraphQLError]);
                    }
                } else if (
                    option === HostedLensDisclaimerEnum.UPDATE_TO_DRAFT ||
                    option === HostedLensDisclaimerEnum.UPDATE_TO_PUBLISHED ||
                    option === HostedLensDisclaimerEnum.GENERATE
                ) {
                    void updateOrGenerateHostedLens();
                }
            }
        },
        [deleteHostedLensLink, handleErrors, hostedLens, option, organizationId, updateOrGenerateHostedLens],
    );

    return (
        <Section
            title={
                <span data-testid="group-info-item-title" className={hostedLensHeaderCss}>
                    <HostedLensDetailsHeader />
                </span>
            }
            subtitle={<HostedLensBody />}
            rightHeaderContent={
                <Flex direction="row" gap="sm" wrap="nowrap">
                    <Button
                        isDisabled={loading || !hostedLens?.id || disableDelete}
                        onClick={() => {
                            showDisclaimerModal(HostedLensDisclaimerEnum.DELETE);
                        }}
                        className={buttonOffset}
                    >
                        <Box ml={{ base: 5, [BREAKPOINT]: 0 }}>
                            <TrashIcon />
                        </Box>
                        <Box visibleFrom={BREAKPOINT}>
                            <DeleteHeader />
                        </Box>
                    </Button>
                    <ButtonDisabledTooltip
                        isDisabled={loading || isUpdating || !!hostedLens?.id || disableGenerate}
                        {...(unlockableId === 0 && {
                            tooltip: {
                                content: <HostedLensDisabledTooltipText />,
                            },
                        })}
                        onClick={() => {
                            showDisclaimerModal(HostedLensDisclaimerEnum.GENERATE);
                        }}
                        className={buttonOffset}
                    >
                        <Box ml={{ base: 5, [BREAKPOINT]: 0 }}>
                            <AddIcon />
                        </Box>
                        <Box visibleFrom={BREAKPOINT}>
                            <GenerateHeader />
                        </Box>
                    </ButtonDisabledTooltip>
                </Flex>
            }
            data-testid="hosted-lens-section"
        >
            {loading ? (
                <SkeletonContainer loading />
            ) : (
                <Flex
                    direction={{ base: "column", [BREAKPOINT]: "row" }}
                    justify="space-between"
                    gap={{ base: "md", [BREAKPOINT]: "sm" }}
                    wrap="wrap"
                    w="100%"
                >
                    <Stack gap="xs" className={bodyContainerCss} maw={250}>
                        <GrayLabel>
                            <SectionLensURLLabel />
                            <Tooltip content={<SectionLensURLTooltipContent />}>
                                <InformationIcon fill={Colors.Gray.V250} />
                            </Tooltip>
                        </GrayLabel>
                        <Flex direction="row" align="center" gap={4}>
                            {hostedLens?.link ? (
                                <>
                                    <Anchor
                                        href={hostedLens.link}
                                        title={hostedLens.link}
                                        referrerPolicy="no-referrer"
                                        data-testid="hosted-lens-link"
                                        target="_blank"
                                    >
                                        {hostedLens.link}
                                    </Anchor>
                                    <CopyToClipboardButton
                                        tooltip={`Copy ${hostedLens.link}`}
                                        copyValue={hostedLens.link}
                                        data-testid="hosted-lens-copy-field"
                                        notification={{ title: `${hostedLens.link} copied to clipboard` }}
                                        testId={hostedLens.link.split(" ").join(".").toLocaleLowerCase()}
                                        iconSize={Sizes.Medium}
                                    />
                                </>
                            ) : (
                                "-"
                            )}
                        </Flex>
                    </Stack>
                    <Stack gap="xs" className={bodyContainerCss} maw={250}>
                        <GrayLabel>
                            <SectionLensStatusLabel />
                            <Tooltip
                                content={
                                    <div className={tooltipStatusWrapper}>
                                        <SectionStatusTooltipContent />
                                        <Flex direction="row" align="center" gap="xs">
                                            <LensStatusIndicator
                                                className={indicatorCSS}
                                                background={Colors.Green.V150}
                                            />
                                            <SectionStatusTooltipItemOne />
                                        </Flex>
                                        <Flex direction="row" align="center" gap="xs">
                                            <LensStatusIndicator
                                                className={indicatorCSS}
                                                background={Colors.Orange.V200}
                                            />
                                            <SectionStatusTooltipItemTwo />
                                        </Flex>
                                    </div>
                                }
                            >
                                <InformationIcon fill={Colors.Gray.V250} />
                            </Tooltip>
                        </GrayLabel>
                        <HostedLensStatusCell
                            data-testid="hosted-lens-status"
                            status={hostedLens?.status ?? HostedLensStatus.HostedLensStatusUnset}
                        />
                    </Stack>
                    <Stack gap="xs" className={bodyContainerCss} maw={250}>
                        <GrayLabel>
                            <SectionLensEnableLabel />
                            <Tooltip content={<SectionLensEnableTooltipContent />}>
                                <InformationIcon fill={Colors.Gray.V250} />
                            </Tooltip>
                        </GrayLabel>
                        <Switch
                            isDisabled={disableGenerate}
                            isChecked={hostedLens?.status === HostedLensStatus.HostedLensStatusPublished}
                            onClick={(isEnabled: boolean) => {
                                handleToggle(isEnabled, hostedLens?.id);
                            }}
                        />
                    </Stack>
                </Flex>
            )}
            <HostedLensDisclaimerModal
                {...showDisclaimerProps}
                loading={isUpdating}
                option={option}
                onAction={onModalAcceptance}
            />
        </Section>
    );
};
